/**
 * The transport. One fetch base — the Edge gateway — and no second one is ever added.
 *
 * Same signature shape as Admin Dashboard/src/api/client.ts:55. Responsibilities:
 * attach the bearer token, serialise params, parse with the generated schema, and hand
 * every non-2xx to mapError.
 *
 * The 401 recovery lives HERE, below the query layer, so every query inherits it and no
 * screen implements it.
 */
import type { z } from "zod";
import { mapError, isNotAnError, type ErrorContext, type MappedFailure } from "./errors.js";

export type TokenProvider = (forceRefresh?: boolean) => Promise<string | null>;

/** The shape portal-gen emits for every operation. */
export interface Operation {
  readonly id: string;
  readonly method: string;
  readonly path: string;
  readonly roleGate: "admin" | "member" | "any";
  readonly pathParams: readonly string[];
  readonly queryParams: Readonly<Record<string, { required: boolean }>>;
  readonly requiresIfMatch: boolean;
  readonly request: z.ZodTypeAny | null;
  readonly successStatus: number;
  readonly response: z.ZodTypeAny | null;
  readonly errors: readonly number[];
}

export interface CallOptions {
  path?: Record<string, string>;
  query?: Record<string, string | number | undefined>;
  body?: unknown;
  /** Policy writes require it; the client refuses to send the request without one. */
  ifMatch?: string;
  ifNoneMatch?: string;
  signal?: AbortSignal;
}

export interface CallResult<T> {
  data: T;
  /** Present on policy reads; drives If-None-Match and the 304 path. */
  etag: string | null;
  status: number;
}

/** Thrown for every non-2xx and every transport failure. Carries no server `message`. */
export class PortalRequestError extends Error {
  readonly failure: MappedFailure;
  constructor(failure: MappedFailure) {
    super(isNotAnError(failure) ? "not-an-error" : `${failure.kind} (${failure.httpStatus ?? "net"})`);
    this.name = "PortalRequestError";
    this.failure = failure;
  }
}

export interface PortalClient {
  call<T>(op: Operation, context: ErrorContext, options?: CallOptions): Promise<CallResult<T>>;
  /** Same as `call`, discarding the envelope. The common case. */
  get<T>(op: Operation, context: ErrorContext, options?: CallOptions): Promise<T>;
}

export function createPortalClient(baseUrl: string, getToken: TokenProvider): PortalClient {
  async function once(
    op: Operation,
    options: CallOptions,
    forceRefresh: boolean,
  ): Promise<Response> {
    const token = await getToken(forceRefresh);
    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (options.ifMatch) headers["If-Match"] = options.ifMatch;
    if (options.ifNoneMatch) headers["If-None-Match"] = options.ifNoneMatch;

    let body: string | undefined;
    if (options.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    }

    return fetch(buildUrl(baseUrl, op, options), {
      method: op.method,
      headers,
      ...(body !== undefined ? { body } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
      credentials: "omit",
      mode: "cors",
    });
  }

  async function call<T>(
    op: Operation,
    context: ErrorContext,
    options: CallOptions = {},
  ): Promise<CallResult<T>> {
    if (op.requiresIfMatch && !options.ifMatch) {
      // Refusing locally is better than a guaranteed 428/409 round trip, and it makes the
      // omission a test failure rather than a production conflict.
      throw new Error(`${op.id} requires If-Match — see docs/POLICY_EDITOR.md §4`);
    }

    // Validate what we are about to send. This catches the orgMode-vs-intent class of bug
    // in a test rather than as a 400 in production.
    if (op.request && options.body !== undefined) {
      const parsed = op.request.safeParse(options.body);
      if (!parsed.success) {
        throw new Error(`${op.id}: request body does not match the contract`);
      }
    }

    let res: Response;
    try {
      res = await once(op, options, false);
      // Exactly one silent, force-refreshed retry. Per-request, not shared, so a burst of
      // parallel calls cannot produce a refresh storm — the SDK's own token cache absorbs it.
      if (res.status === 401) {
        res = await once(op, options, true);
      }
    } catch (e) {
      throw new PortalRequestError(mapError(e instanceof Error ? e : new Error("fetch failed"), context));
    }

    const etag = res.headers.get("ETag");

    if (res.status === 304) {
      // The caller keeps its cached document. Signalled by a null data slot.
      return { data: undefined as T, etag, status: 304 };
    }

    if (!res.ok) {
      // Read only `code`. The envelope's `message` is operator-facing and is dropped here,
      // at the boundary, so it cannot reach a component even by accident.
      let code: string | undefined;
      try {
        const json = (await res.json()) as { code?: unknown };
        if (typeof json?.code === "string") code = json.code;
      } catch {
        /* an unparseable error body is still an error */
      }
      throw new PortalRequestError(mapError({ status: res.status, body: { code } }, context));
    }

    if (res.status === 204 || op.response === null) {
      return { data: undefined as T, etag, status: res.status };
    }

    const json = await res.json();
    const parsed = op.response.safeParse(json);
    if (!parsed.success) {
      // A .strict() failure is a SECURITY outcome, not a typing inconvenience: a response
      // that grew a content-bearing field must fail here rather than render. It is never
      // presented as data.
      throw new PortalRequestError(
        mapError({ status: 502, body: { code: "SCHEMA_MISMATCH" } }, context),
      );
    }
    return { data: parsed.data as T, etag, status: res.status };
  }

  return {
    call,
    async get<T>(op: Operation, context: ErrorContext, options?: CallOptions): Promise<T> {
      return (await call<T>(op, context, options)).data;
    },
  };
}

/** Path templating + query serialisation. No PII ever reaches either. */
export function buildUrl(baseUrl: string, op: Operation, options: CallOptions): string {
  let path = op.path;
  for (const name of op.pathParams) {
    const value = options.path?.[name];
    if (value === undefined) throw new Error(`${op.id}: missing path parameter ${name}`);
    path = path.replace(`{${name}}`, encodeURIComponent(value));
  }
  // `:reset` style suffixes are literal; anything left in braces is a wiring bug.
  if (/\{[^}]+\}/.test(path)) throw new Error(`${op.id}: unfilled path template ${path}`);

  const url = new URL(baseUrl + path);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value === undefined || value === "") continue;
    if (!(key in op.queryParams)) throw new Error(`${op.id}: ${key} is not a query parameter`);
    url.searchParams.set(key, String(value));
  }
  for (const [key, spec] of Object.entries(op.queryParams)) {
    if (spec.required && !url.searchParams.has(key)) {
      throw new Error(`${op.id}: missing required query parameter ${key}`);
    }
  }
  return url.toString();
}
